/**
 * src/core/validator.ts
 *
 * Responsável por validar a integridade e segurança do JSON de configuração
 * de um cenário interativo.
 *
 * Este módulo atua como a "linha de defesa" antes da renderização:
 * - valida schema mínimo (campos obrigatórios),
 * - aplica regras de negócio,
 * - filtra hotspots inseguros ou inconsistentes,
 * - garante que apenas dados confiáveis cheguem à UI.
 *
 * Filosofia:
 * - Erros críticos (schema raiz) impedem a renderização do cenário.
 * - Erros locais (hotspots inválidos) geram warnings e são ignorados
 *   individualmente (FAIL PARTIAL).
 */

import { CenarioConfig, Hotspot, ValidationResult } from './types';

/**
 * Valida um objeto de configuração de cenário.
 *
 * @param data Objeto JSON bruto (normalmente carregado da CDN).
 * @returns Resultado da validação contendo:
 * - `isValid`: se o cenário pode ser renderizado,
 * - `errors`: erros críticos (bloqueiam renderização),
 * - `warnings`: problemas não críticos,
 * - `validHotspots`: hotspots seguros para uso.
 */
export function validateConfig(data: unknown): ValidationResult {
  const result: ValidationResult = {
    isValid: false,
    errors: [],
    warnings: [],
    validHotspots: [],
  };

  // ========================================================================
  // 1. VALIDAÇÃO DE SCHEMA (CRÍTICO)
  // Qualquer falha aqui impede completamente a renderização do cenário.
  // ========================================================================

  if (!data || typeof data !== 'object') {
    result.errors.push('Configuração nula ou inválida.');
    return result;
  }

  const config = data as Partial<CenarioConfig>;

  if (!config.id || config.id.trim() === '') {
    result.errors.push("O campo 'id' é obrigatório.");
  }

  if (!config.image || config.image.trim() === '') {
    result.errors.push("O campo 'image' (URL) é obrigatório.");
  }

  if (typeof config.width !== 'number' || config.width <= 0) {
    result.errors.push("Dimensão 'width' inválida. Deve ser um número positivo.");
  }

  if (typeof config.height !== 'number' || config.height <= 0) {
    result.errors.push("Dimensão 'height' inválida. Deve ser um número positivo.");
  }

  if (!Array.isArray(config.hotspots)) {
    result.errors.push("O campo 'hotspots' deve ser um array.");
  }

  // Erros críticos → aborta imediatamente
  if (result.errors.length > 0) {
    return result;
  }

  // ========================================================================
  // 2. VALIDAÇÃO DE HOTSPOTS (FAIL PARTIAL)
  // Cada hotspot é avaliado isoladamente.
  // ========================================================================

  config.hotspots!.forEach((rawHotspot, index) => {
    const h = rawHotspot as Partial<Hotspot>;
    const idRef = h.id ?? `index-${index}`;
    let isHotspotValid = true;

    // --- Identificação ---
    // ID ausente não bloqueia, mas gera aviso.
    if (!h.id) {
      result.warnings.push(`Hotspot [${index}] sem ID. Gerado automaticamente: ${idRef}.`);
    }

    // --- Campos obrigatórios ---
    // `title` é obrigatório por UX e A11Y (tooltip e fallback semântico).
    if (!h.title || h.title.trim() === '') {
      result.warnings.push(`Hotspot '${idRef}' ignorado: campo 'title' é obrigatório.`);
      isHotspotValid = false;
    }

    // --- Geometria ---
    // Deve possuir polígono OU retângulo.
    const hasPolygon = Array.isArray(h.points) && h.points.length >= 3;
    const hasRect =
      typeof h.x === 'number' &&
      typeof h.y === 'number' &&
      typeof h.width === 'number' &&
      typeof h.height === 'number';

    if (!hasPolygon && !hasRect) {
      result.warnings.push(
        `Hotspot '${idRef}' ignorado: Geometria inválida (nem polígono nem retângulo).`
      );
      isHotspotValid = false;
    }

    // --- Bounds Check ---
    // Impede hotspots fora da imagem base.
    if (isHotspotValid && hasRect) {
      if (
        h.x! < 0 ||
        h.y! < 0 ||
        h.width! <= 0 ||
        h.height! <= 0 ||
        h.x! + h.width! > config.width! ||
        h.y! + h.height! > config.height!
      ) {
        result.warnings.push(`Hotspot '${idRef}' fora dos limites da imagem.`);
        isHotspotValid = false;
      }
    }

    if (isHotspotValid && hasPolygon) {
      const outOfBounds = h.points!.some(
        ([x, y]) => x < 0 || y < 0 || x > config.width! || y > config.height!
      );
      if (outOfBounds) {
        result.warnings.push(`Hotspot '${idRef}' (polígono) possui pontos fora da imagem.`);
        isHotspotValid = false;
      }
    }

    // --- Modal ---
    // Modal sem conteúdo é considerado inválido.
    if (isHotspotValid && h.action === 'modal') {
      if (!h.modalData || !h.modalData.image) {
        result.warnings.push(`Hotspot '${idRef}' configurado como modal sem 'modalData' válido.`);
        isHotspotValid = false;
      }
    }

    // --- Integração Moodle ---
    // Erro aqui não invalida o hotspot (pode existir fallback).
    if (isHotspotValid && h.moodle?.type === 'activity') {
      if (typeof h.moodle.activityId !== 'number') {
        result.warnings.push(`Hotspot '${idRef}': 'activityId' inválido.`);
      }
    }

    // --- Acessibilidade (A11Y) ---
    // ariaLabel é obrigatório, mas aceita fallback automático.
    if (isHotspotValid && (!h.ariaLabel || h.ariaLabel.trim() === '')) {
      result.warnings.push(
        `Hotspot '${idRef}' sem 'ariaLabel'. Usando 'title' ou ID como fallback.`
      );
      h.ariaLabel = h.title || `Botão ${idRef}`;
    }

    // Hotspot aprovado
    if (isHotspotValid) {
      result.validHotspots.push(h as Hotspot);
    }
  });

  result.isValid = true;
  return result;
}
